import { useState } from 'react';
import { formatRial } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import {
  Button,
  Card,
  Chip,
  ErrorRetry,
  Field,
  Input,
  NumberInput,
  useConfirm,
  useToast,
} from '../ui';

/**
 * دارایی ثابت و استهلاک (fixed assets + depreciation) — asset register + a monthly straight-line
 * depreciation run that, once posted, books Dr Depreciation expense / Cr Accumulated depreciation.
 * Finance/Admin (accounting.post to add / run / post).
 */

interface Asset {
  id: number;
  code: string;
  name: string;
  cost: number;
  accumulated: number;
  bookValue: number;
  status: string;
}
interface Run {
  id: number;
  no: number;
  ym: string;
  status: string;
  total: number;
}

export function AssetsPanel({ onPosted }: { onPosted?: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const assetsRes = useApiResource<{ assets: Asset[] }>('/api/accounting/assets');
  const runsRes = useApiResource<{ runs: Run[] }>('/api/accounting/depreciation');
  const assets = assetsRes.data?.assets ?? [];
  const runs = runsRes.data?.runs ?? [];
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [cost, setCost] = useState(0);
  const [salvage, setSalvage] = useState(0);
  const [lifeMonths, setLifeMonths] = useState(60);
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));

  const refresh = () => {
    assetsRes.reload();
    runsRes.reload();
  };

  async function addAsset() {
    if (!name.trim() || cost < 0 || lifeMonths <= 0) {
      toast('نام، بها و عمر مفید لازم است', 'error');
      return;
    }
    setBusy(true);
    const r = await api('/api/accounting/assets', {
      method: 'POST',
      body: JSON.stringify({ name, cost, salvage, lifeMonths }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r, 'خطا'), 'error');
    toast('دارایی افزوده شد ✓');
    setName('');
    setCost(0);
    setSalvage(0);
    refresh();
  }

  async function run() {
    setBusy(true);
    const r = await api('/api/accounting/depreciation', {
      method: 'POST',
      body: JSON.stringify({ ym }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r, 'خطا در محاسبهٔ استهلاک'), 'error');
    toast('استهلاک ماه محاسبه شد ✓');
    refresh();
  }

  async function post(id: number) {
    if (
      !(await confirm({
        title: 'ثبت استهلاک در دفترکل',
        message:
          'هزینهٔ استهلاک این دوره در دفترکل ثبت می‌شود (قابل بازگشت فقط با سند برگشتی). ادامه؟',
        danger: true,
      }))
    )
      return;
    setBusy(true);
    const r = await api(`/api/accounting/depreciation/${id}/post`, { method: 'POST' });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r, 'خطا در ثبت'), 'error');
    toast('استهلاک به دفترکل ثبت شد ✓');
    refresh();
    onPosted?.();
  }

  return (
    <Card title="دارایی ثابت و استهلاک">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))',
          gap: 10,
          alignItems: 'end',
        }}
      >
        <Field label="نام دارایی">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="بهای تمام‌شده">
          <NumberInput value={cost} onValue={setCost} />
        </Field>
        <Field label="ارزش اسقاط">
          <NumberInput value={salvage} onValue={setSalvage} />
        </Field>
        <Field label="عمر مفید (ماه)">
          <NumberInput value={lifeMonths} onValue={setLifeMonths} />
        </Field>
        <Button variant="secondary" onClick={() => void addAsset()} disabled={busy}>
          افزودن دارایی
        </Button>
      </div>

      {assetsRes.status === 'error' || runsRes.status === 'error' ? (
        <ErrorRetry error={assetsRes.error ?? runsRes.error} onRetry={refresh} />
      ) : null}

      {assets.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ marginTop: 10 }}>
            <thead>
              <tr>
                <th>دارایی</th>
                <th>بهای تمام‌شده</th>
                <th>استهلاک انباشته</th>
                <th>ارزش دفتری</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td>{formatRial(a.cost)}</td>
                  <td>{formatRial(a.accumulated)}</td>
                  <td>
                    <b>{formatRial(a.bookValue)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'end', marginTop: 12, flexWrap: 'wrap' }}>
        <Field label="ماه (YYYY-MM)">
          <Input value={ym} onChange={(e) => setYm(e.target.value)} placeholder="2026-07" />
        </Field>
        <Button onClick={() => void run()} disabled={busy}>
          محاسبهٔ استهلاک ماه
        </Button>
      </div>

      {runs.length > 0 ? (
        <table className="table" style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>ماه</th>
              <th>مبلغ استهلاک</th>
              <th>وضعیت</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}>
                <td>{r.ym}</td>
                <td>{formatRial(r.total)}</td>
                <td>
                  <Chip tone={r.status === 'posted' ? 'success' : 'neutral'}>
                    {r.status === 'posted' ? 'ثبت‌شده' : 'پیش‌نویس'}
                  </Chip>
                </td>
                <td>
                  {r.status === 'draft' ? (
                    <Button variant="secondary" onClick={() => void post(r.id)} disabled={busy}>
                      ثبت در دفترکل
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Card>
  );
}
